import "./__styles__/SharePropertyInfoModal.scss";
import React from "react";
import { gql } from "@apollo/client";
import { useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { PROPERTY_OBJECT_LABEL } from "common/constants";
import { isValidEmail } from "common/utils/strings";
import { buildGuestPropertyURL } from "common/utils/url";
import {
  InformationRequestContactType,
  useSharePropertyMutation,
} from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { track } from "../../../utils/tracking";
import { AuthContext } from "../../Authorization/AuthContext";
import {
  ButtonSection,
  Container,
  ContentSection,
  FormSection,
  HeaderSection,
  InputRow,
  PrimaryButtons,
  SecondaryButtons,
} from "../../Common/__styles__/Modal";
import { Button } from "../../Common/Button";
import Modal from "../../Common/Modal";
import { SuccessView } from "../../Common/ShareItem/SuccessView";
import { CONTACT_TYPE_OPTIONS } from "../../Common/ShareItem/utils";
import { Body } from "../../Common/Typography";
import { Text as NonHookText } from "../../Inputs";
import { Checkbox, Select, Text } from "../../Inputs/react-hook-form";

type SharePropertyLog = { id: string };

export const SHARE_PROPERTY = gql`
  mutation ShareProperty($data: PropertyShareInput!) {
    shareProperty(data: $data) {
      log {
        id
      }
    }
  }
`;

interface SharePropertyModalHook {
  onSubmit: () => void;
  onCancel: () => void;
  property: any;
}
export const useSharePropertyInfoModal = ({
  onSubmit,
  onCancel,
  property,
}: SharePropertyModalHook) => {
  const [showSharePropertyModal, hideSharePropertyModal] = useModal(
    () => (
      <Modal
        onRequestClose={() => {
          onCancel();
        }}
      >
        <SharePropertyInfoForm
          onSubmit={onSubmit}
          onCancel={onCancel}
          property={property}
        />
      </Modal>
    ),
    [onSubmit, property],
  );

  return [showSharePropertyModal, hideSharePropertyModal] as const;
};

type SharePropertyFormStructure = {
  property: string;
  recipientFirstName: string;
  recipientLastName: string;
  recipientEmail: string;
  recipientType: InformationRequestContactType;
  copyYourself: boolean;
  createLog: boolean;
};

export type SharePropertyInfoFormProps = {
  clipboard?: Pick<typeof navigator.clipboard, "writeText">;
  urlOpener?: (path: string, mode: string) => void;
} & SharePropertyModalHook;
const SharePropertyInfoForm = ({
  onCancel,
  property,
  urlOpener = window.open,
  clipboard = navigator.clipboard,
}: SharePropertyInfoFormProps) => {
  const { account, user, admin } = React.useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [residentLog, setResidentLog] =
    React.useState<Maybe<SharePropertyLog>>(null);

  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
  } = useForm<SharePropertyFormStructure>({
    defaultValues: {
      recipientType: InformationRequestContactType.PROPERTY_OWNER,
      createLog: true,
      copyYourself: true,
    },
  });

  const [shareProperty, { loading }] = useSharePropertyMutation({
    onCompleted: response => {
      let log: Maybe<SharePropertyLog> = null;
      if (response?.shareProperty?.log?.id) {
        log = { id: response.shareProperty.log.id };
      }

      setResidentLog(log);
      setShowSuccess(true);
    },
    onError: () => {
      addErrorToast(
        `There was an error sharing this property. Please try again. If the problem persists, please email us at support@withforerunner.com`,
      );
    },
  });

  const onSubmit = async (formData: SharePropertyFormStructure) => {
    if (loading) return;

    const data = {
      ...formData,
      propertyId: property.id,
    };

    track("Emailed Link", {
      streetAddress: property.streetAddress,
      city: property.city,
      logCreated: data.createLog,
      userCCd: data.copyYourself,
    });
    await shareProperty({ variables: { data } });
  };

  if (showSuccess) {
    return (
      <SuccessView
        headerText="Share property info"
        recipientEmail={getValues("recipientEmail")}
        residentLogId={residentLog?.id}
        onClose={onCancel}
      />
    );
  }

  const propertyURL = buildGuestPropertyURL({
    subdomain: account!.subdomain,
    propertyId: property.id,
  });

  return (
    <Container overflows>
      <HeaderSection>
        <h1>Share property info</h1>
        <h2>Send a public property profile link to a recipient</h2>
      </HeaderSection>
      <FormSection overflows>
        <ContentSection overflows>
          <Body size="default" type="regular">
            The recipient will receive an email with a link to a public profile
            of the property. This public link will not allow the recipient to
            edit or delete any information.
          </Body>
          <InputRow>
            <NonHookText
              name="property"
              label={PROPERTY_OBJECT_LABEL}
              value={property?.fullAddress || "No address for property"}
              disabled={true}
              size="small"
            />
          </InputRow>
          <InputRow>
            <Text
              {...register("recipientFirstName", {
                required: "Recipient's first name is required",
              })}
              error={errors.recipientFirstName?.message}
              size="small"
              label="Recipient first name"
              required
            />
            <Text
              {...register("recipientLastName", {
                required: "Recipient's last name is required",
              })}
              error={errors.recipientLastName?.message}
              size="small"
              label="Recipient last name"
              required
            />
          </InputRow>
          <InputRow style={{ lineHeight: "1" }}>
            <Text
              {...register("recipientEmail", {
                validate: val => {
                  if (!val) {
                    return "Required field";
                  }
                  if (!isValidEmail(val)) {
                    return "Email is invalid";
                  }
                  return;
                },
              })}
              error={errors.recipientEmail?.message}
              size="small"
              label="Recipient email"
              required
            />
            <Select
              control={control}
              name="recipientType"
              label="Recipient type"
              required
              options={CONTACT_TYPE_OPTIONS}
            />
          </InputRow>
          <fieldset styleName="fieldset">
            <Body size="default" type="emphasis">
              Additional actions
            </Body>
            <Checkbox
              control={control}
              name="copyYourself"
              label={`Copy yourself on email (${user?.email ?? admin?.email})`}
            />
            <Checkbox
              control={control}
              name="createLog"
              label="Create new resident log"
            />
          </fieldset>
        </ContentSection>
        <ButtonSection>
          <SecondaryButtons>
            <Button
              onClick={() => {
                track("Clicked view property button", {
                  streetAddress: property.streetAddress,
                  city: property.city,
                });

                urlOpener(propertyURL, "_blank");
              }}
              size="medium"
              styleVariant="ghost"
              leftIconName="eye"
              type="button"
            >
              View public profile
            </Button>
            <Button
              onClick={async () => {
                track("Copied Link", {
                  streetAddress: property.streetAddress,
                  city: property.city,
                });

                await clipboard.writeText(propertyURL);
                addSuccessToast("Link copied to clipboard");
              }}
              size="medium"
              styleVariant="ghost"
              leftIconName="link"
              type="button"
            >
              Copy url link
            </Button>
          </SecondaryButtons>
          <PrimaryButtons>
            <Button
              onClick={handleSubmit(onSubmit)}
              disabled={loading}
              size="medium"
              styleVariant="primary"
              type="button"
            >
              Send
            </Button>
          </PrimaryButtons>
        </ButtonSection>
      </FormSection>
    </Container>
  );
};

export default SharePropertyInfoForm;
